import { useCallback, useEffect, useRef, useState } from 'react';

import { type MessageDescriptor, defineMessages, useIntl } from 'react-intl-next';

import { getDocument } from '@atlaskit/browser-apis';
import { usePlatformInstrumentation } from '@atlassian/platform-analytics';

import type { ActiveContent } from '../../types/activeContent';
import { AUDIO_PLAYER_OPEN_REQUEST } from '../hooks/useAudioPlayerIframeListener';
import { useAudioPlayerStore } from '../hooks/useAudioPlayerStore';
import { useAudioPlayerStreamingStore } from '../hooks/useAudioPlayerStreamingStore';
import { useAudioShareLink } from '../hooks/useAudioShareLink';
import { AudioStreamingState } from '../state/AudioPlayerStreamingStore';

export interface UseAudioListenTriggerOptions {
	activeContent: ActiveContent;
	source: string;
	isDisabled?: boolean;
	isAutoOpenDisabled?: boolean;
	playImmediately?: boolean;
}

export interface UseAudioListenTriggerResult {
	handleClick: () => void;
	isAudioSupported: boolean;
	tooltipContent: string;
	buttonRef: React.RefObject<HTMLButtonElement>;
	label: string;
}

const i18n = defineMessages({
	listen: {
		id: 'audio.audio-player.audio-listen-button.listen',
		defaultMessage: 'Listen',
		description: 'Label and tooltip for the audio listen button',
	},
	listenToContent: {
		id: 'audio.audio-player.audio-listen-button.listen',
		defaultMessage: 'Listen to {contentType}',
		description:
			'Label and tooltip for the audio listen button where content type can be page, live doc, or blog post. Used in combination with the contentTypePage and contentTypeBlogPost messages to create the full label and tooltip.',
	},
	browserNotSupported: {
		id: 'audio.audio-player.audio-listen-button.browserNotSupported',
		defaultMessage: 'Your browser doesn’t support this feature',
		description:
			'Message in tooltip which tells user that their browser does not support Audio features',
	},
	contentTypePage: {
		id: 'audio.audio-player.audio-listen-button.contentTypePage',
		defaultMessage: 'page',
		description:
			'Label and tooltip for the audio listen button when the content type is page. Used in combination with the listenToContent message to create the full label and tooltip. In this case the full text would read as "Listen to page"',
	},
	contentTypeBlogPost: {
		id: 'audio.audio-player.audio-listen-button.contentTypeBlogPost',
		defaultMessage: 'blogpost',
		description:
			'Label and tooltip for the audio listen button when the content type is blog post. Used in combination with the listenToContent message to create the full label and tooltip. In this case the full text would read as "Listen to blogpost"',
	},
	contentTypeLiveDoc: {
		id: 'audio.audio-player.audio-listen-button.contentTypeLiveDoc',
		defaultMessage: 'live doc',
		description:
			'Label and tooltip for the audio listen button when the content subtype is live. Used in combination with the listenToContent message to create the full label and tooltip. In this case the full text would read as "Listen to live doc"',
	},
});

/**
 *
 * This hook extracts the core logic for the audio popup to allow consumers
 * to build custom trigger UIs while maintaining consistent behavior. It provides
 * logic for triggering audio playback, handles browser support detection,
 * sending analytics, and share link handling.
 *
 * @param options - Configuration options for the audio listen trigger
 * @returns All the props and handlers needed to build a custom audio trigger UI
 */
export const useAudioListenTrigger = ({
	activeContent,
	source,
	isDisabled = false,
	isAutoOpenDisabled = false,
	playImmediately = false,
}: UseAudioListenTriggerOptions): UseAudioListenTriggerResult => {
	// Assume audio is enabled for SSR until client side useEffect checks browser support.
	// This is to avoid expensive hydration issues since in most cases audio will indeed be supported.
	const [audioSupportedByBrowser, setAudioSupportedByBrowser] = useState<boolean>(true);

	const { formatMessage, locale } = useIntl();
	const { sendUIEvent, sendScreenEvent } = usePlatformInstrumentation();
	const buttonRef = useRef<HTMLButtonElement>(null);

	const { openAudioPlayer, isOpen } = useAudioPlayerStore();
	const { state: streamingState } = useAudioPlayerStreamingStore();
	const isUninitialized = streamingState.state === AudioStreamingState.UNINITIALIZED;

	// Handle audio share links from URL parameters
	useAudioShareLink(activeContent, isUninitialized, isAutoOpenDisabled);

	// Check browser capability for audio support
	useEffect(() => {
		// Skip browser capability check during SSR since MediaSource doesn't exist on the server.
		// Note: Confluence uses a special SSR implementation where useEffect runs on the server.
		// therefore this extra defensive check is needed
		// More info can be found here: https://hello.atlassian.net/wiki/spaces/CCPERF/blog/2025/01/04/4610696218/SSR+ing+Confluence+Editor+via+Full+Cycle+Rendering
		if (process.env.REACT_SSR) {
			return;
		}

		const isAudioSupportedByBrowser =
			typeof MediaSource !== 'undefined' && MediaSource.isTypeSupported('audio/mpeg');
		if (!isAudioSupportedByBrowser) {
			setAudioSupportedByBrowser(false);
			sendScreenEvent({
				eventType: 'screen',
				name: 'audioButtonDisabled',
				attributes: {
					source,
					locale,
					version: 'v3',
				},
			});
		} else {
			sendScreenEvent({
				eventType: 'screen',
				name: 'audioButton',
				attributes: {
					source,
					locale,
					version: 'v3',
				},
			});
		}
	}, [sendScreenEvent, locale, source]);

	const sendAnalyticsUIEvent = useCallback(
		(action: string) => {
			sendUIEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'audioPopup',
				source,
				attributes: {
					action,
					contentType: activeContent.contentType ?? 'page',
					// TODO: Determine if pageMode is needed here as it was included within v2
					// analytics but requires a Confluence dependency
				},
			});
		},
		[sendUIEvent, activeContent, source],
	);

	const handleClick = useCallback(() => {
		if (!isOpen) {
			sendAnalyticsUIEvent('openPopup');
		}

		// When rendered inside an embedded iframe (e.g. a smart-link embed), the audio player lives in the parent frame.
		// Send a postMessage so the parent can open its own AudioContainer.
		// Note, `returnFocusRef` is intentionally excluded since it cannot be serialized by postMessage.
		if (window.parent !== window) {
			const document = getDocument();

			if (!document) {
				return;
			}

			const targetOrigin = document.referrer ? new URL(document.referrer).origin : '*';

			window.parent.postMessage(
				{
					type: AUDIO_PLAYER_OPEN_REQUEST,
					activeContent,
					options: { isUninitialized, playImmediately },
				},
				targetOrigin,
			);
			return;
		}

		openAudioPlayer(activeContent, {
			isUninitialized,
			returnFocusRef: buttonRef,
			playImmediately,
		});
	}, [
		isOpen,
		sendAnalyticsUIEvent,
		openAudioPlayer,
		activeContent,
		isUninitialized,
		playImmediately,
	]);

	const contentTypeMessages: Record<string, MessageDescriptor> = {
		live: i18n.contentTypeLiveDoc,
		page: i18n.contentTypePage,
		blogpost: i18n.contentTypeBlogPost,
	};

	const key = activeContent.subType || activeContent.contentType;
	const contentTypeMessage = key ? contentTypeMessages[key] : undefined;
	const contentType = contentTypeMessage ? formatMessage(contentTypeMessage) : '';

	const label = contentType
		? formatMessage(i18n.listenToContent, { contentType })
		: formatMessage(i18n.listen);

	const tooltipContent = audioSupportedByBrowser ? label : formatMessage(i18n.browserNotSupported);

	return {
		handleClick,
		isAudioSupported: !isDisabled && audioSupportedByBrowser,
		tooltipContent,
		buttonRef,
		label,
	};
};
